/**
 * Manages user order keys for MAC verification.
 * Keys are stored in a 2-element array indexed by epoch % 2.
 * Keys are pre-converted to siphash format (4 x 32-bit LE integers) for efficiency.
 */

import * as siphash from 'siphash';
import { useUserStore } from '@/stores/user';

// Siphash key format: 4 x 32-bit little-endian integers
type SiphashKey = [number, number, number, number];

// Order keys indexed by epoch % 2, stored in siphash-ready format
const orderKeys: (SiphashKey | null)[] = [null, null];

/**
 * Gets the current user ID from the user store.
 * Lazily accesses the store to avoid issues with Pinia not being initialized at module load.
 */
function getCurrentUserId(): string | null {
  try {
    // Dynamic import to avoid circular deps and module load timing issues
    const userStore = useUserStore();
    // @ts-ignore No types on store
    return userStore.user?.id?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Converts a 16-byte key to the format expected by siphash.
 * siphash expects an array of 4 32-bit little-endian numbers.
 */
function toSiphashKey(key: Uint8Array): SiphashKey {
  const view = new DataView(key.buffer, key.byteOffset, key.byteLength);
  return [
    view.getUint32(0, true),
    view.getUint32(4, true),
    view.getUint32(8, true),
    view.getUint32(12, true),
  ];
}

/**
 * Converts a Uint8Array to a hex string for logging.
 */
function uint8ArrayToHex(arr: Uint8Array): string {
  return Array.from(arr)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Stores an order key for the given epoch.
 * Keys are stored at index epoch % 2, pre-converted to siphash format.
 */
export function storeOrderKey(epoch: number, key: Uint8Array): void {
  const index = epoch % 2;
  orderKeys[index] = toSiphashKey(key);
}

/**
 * Gets the order key for the given epoch.
 */
function getOrderKey(epoch: number): SiphashKey | null {
  return orderKeys[epoch % 2];
}

/**
 * Computes the MAC for an order ID using the key for the given epoch.
 * Returns the MAC as a 16-character hex string, or null if no key is available.
 */
/**
 * Converts a SiphashKey back to a hex string for logging.
 */
function siphashKeyToHex(key: SiphashKey): string {
  const buffer = new ArrayBuffer(16);
  const view = new DataView(buffer);
  view.setUint32(0, key[0], true);
  view.setUint32(4, key[1], true);
  view.setUint32(8, key[2], true);
  view.setUint32(12, key[3], true);
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function computeMac(epoch: number, orderId: string): string | null {
  const key = getOrderKey(epoch);
  if (!key) {
    console.log(`[OrderKey] computeMac: no key for epoch ${epoch}`);
    return null;
  }

  const result = siphash.hash(key, orderId);

  // Convert to hex string (16 chars for 64-bit)
  const high = (result.h >>> 0).toString(16).padStart(8, '0');
  const low = (result.l >>> 0).toString(16).padStart(8, '0');
  const mac = high + low;

  return mac;
}

/**
 * Verifies if an order belongs to the current user by checking its MAC.
 * If the MAC matches, returns the current user ID. Otherwise returns undefined.
 */
export function verifyOrderMac(
  epoch: number,
  orderId: string,
  mac: string | undefined | null,
): string | undefined {
  const currentUserId = getCurrentUserId();
  if (!mac || !currentUserId) {
    console.log(`[OrderKey] verifyOrderMac skipped: mac=${mac ? 'present' : 'missing'}, userId=${currentUserId ? 'set' : 'not set'}`);
    return undefined;
  }

  const computedMac = computeMac(epoch, orderId);

  if (computedMac === mac) {
    return currentUserId;
  }

  return undefined;
}

/**
 * Clears all stored keys. Call on logout.
 */
export function clearOrderKeys(): void {
  orderKeys[0] = null;
  orderKeys[1] = null;
  console.log('[OrderKey] Cleared all keys');
}
